// Word-level diff for creative copy, so a versions timeline can show what
// moved between two shapes without a library.
//
// Tokens are words and the whitespace between them (kept, so the rendered
// diff reads like the original). Longest-common-subsequence over the tokens;
// beyond `MAX_TOKENS` per side the whole field is shown as removed + added,
// which is still correct, just coarser.

export type DiffToken = { kind: 'same' | 'del' | 'ins'; text: string };

const MAX_TOKENS = 600;

function tokenize(text: string): string[] {
  return text.split(/(\s+)/).filter((token) => token.length > 0);
}

export function diffWords(before: string, after: string): DiffToken[] {
  if (before === after) return before ? [{ kind: 'same', text: before }] : [];
  const a = tokenize(before);
  const b = tokenize(after);
  if (a.length > MAX_TOKENS || b.length > MAX_TOKENS) {
    const out: DiffToken[] = [];
    if (before) out.push({ kind: 'del', text: before });
    if (after) out.push({ kind: 'ins', text: after });
    return out;
  }
  // lcs[i][j] = length of the LCS of a[i..] and b[j..]
  const lcs: number[][] = Array.from({ length: a.length + 1 }, () =>
    new Array<number>(b.length + 1).fill(0),
  );
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      lcs[i]![j] =
        a[i] === b[j] ? lcs[i + 1]![j + 1]! + 1 : Math.max(lcs[i + 1]![j]!, lcs[i]![j + 1]!);
    }
  }
  const out: DiffToken[] = [];
  const push = (kind: DiffToken['kind'], text: string) => {
    const last = out[out.length - 1];
    if (last && last.kind === kind) last.text += text;
    else out.push({ kind, text });
  };
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      push('same', a[i]!);
      i++;
      j++;
    } else if (lcs[i + 1]![j]! >= lcs[i]![j + 1]!) {
      push('del', a[i]!);
      i++;
    } else {
      push('ins', b[j]!);
      j++;
    }
  }
  while (i < a.length) push('del', a[i++]!);
  while (j < b.length) push('ins', b[j++]!);
  return out;
}
